import { type Decimal, dec } from "./decimal";
import { UNITS } from "./units";

/**
 * When not to repitch (Scott's Jam, 2026-10-08): "once a yeast is used once it can be repitched from tank
 * to tank. sometimes you will not want to repitch if the gravity is to high, beer is too hoppy or the
 * yeast has been stagnant for too long without being fed." A beer whose yeast could be pitched again is
 * judged on those three: its original gravity, how hard it was dry hopped, and how long since its yeast
 * was last fed, counted from its brew day while it is still in the fermenter and from its harvest once it
 * is in a brink. Past a brewery's limit is a warning, never a refusal: the brewer decides.
 */

/** A brewery's limits, from Settings, Equipment, Cellar; null where it never wants the warning. */
export interface RepitchLimits {
  /** An original gravity, 1.065. */
  og: Decimal | null;
  /** Dry hop, grams per liter of the batch (canonical; shown as lb/bbl). */
  dryHopGramsPerLiter: Decimal | null;
  /** Days since its yeast was last fed. */
  days: number | null;
}

/** A pound of hops to the barrel of beer, in grams per liter. */
export const LB_PER_BBL = dec(UNITS.lb.factor).div(dec(UNITS.bbl.factor));

/**
 * The limits a brewery starts with until it sets its own: Claude's suggestion (2026-10-09), Scott to
 * say his own numbers. Above 1.065 yeast comes out stressed; a pound a barrel of dry hop leaves the cone
 * full of hop matter; two weeks is as long as most breweries leave yeast before pitching it.
 */
export const USUAL_REPITCH_LIMITS = {
  og: "1.065",
  dryHopLbPerBbl: "1",
  days: 14,
} as const;

/** What a beer's yeast has been through, as its warnings judge it. */
export interface RepitchFacts {
  /** Its original gravity: measured on brew day, else its recipe's estimate. */
  og: number | null;
  /** Its dry hop, grams per liter of the batch, as its recipe asks. */
  dryHopGramsPerLiter: number;
  /** Days since its yeast was last fed. */
  days: number;
}

export type RepitchWarning = "og" | "dry_hop" | "days";

/** The limits a beer's yeast is past, in the order brew day says them. */
export function repitchWarnings(facts: RepitchFacts, limits: RepitchLimits): RepitchWarning[] {
  const out: RepitchWarning[] = [];
  if (limits.og !== null && facts.og !== null && dec(facts.og.toFixed(6)).gt(limits.og)) {
    out.push("og");
  }
  if (
    limits.dryHopGramsPerLiter !== null &&
    dec(facts.dryHopGramsPerLiter.toFixed(6)).gt(limits.dryHopGramsPerLiter)
  ) {
    out.push("dry_hop");
  }
  if (limits.days !== null && facts.days > limits.days) out.push("days");
  return out;
}

/** Grams per liter as pounds to the barrel, and back. */
export const lbPerBbl = (gramsPerLiter: Decimal | string): Decimal =>
  dec(gramsPerLiter).div(LB_PER_BBL);
export const gramsPerLiterOf = (lbPerBblValue: Decimal | string): Decimal =>
  dec(lbPerBblValue).times(LB_PER_BBL);
